export type Operator = "EQ" | "GT" | "GE" | "LT" | "LE";

export type Filter = (
  columnVal: string,
  operator: Operator,
  operand: string
) => boolean;

export interface ColumnFilter {
  columnKey: string;
  operator: Operator;
  operand: string;
  filterType?: Filter; // If not present, default to SimpleFilter
}

export interface RowFilter {
  columnFilters: ColumnFilter[];
}

export const SimpleFilter: Filter = (
  columnVal: string,
  operator: Operator,
  operand: string
) => {
  switch (operator) {
    case "EQ":
      return columnVal === operand;
    case "GT":
      return columnVal > operand;
    case "GE":
      return columnVal >= operand;
    case "LT":
      return columnVal < operand;
    case "LE":
      return columnVal <= operand;
  }
  throw new Error("Unrecognised operator: " + operator);
};

type Row = { [key: string]: any };
const filterColumnUsingConfig = (
  row: Row,
  config: ColumnFilter,
  variables?: { [key: string]: string }
): boolean => {
  function expandFilter(expr: string) {
    if (!variables) {
      return expr;
    }
    const variableNames = expr.match(/(?<=\[)([^\]]*)(?=\])/g);
    return !!variableNames
      ? variableNames.reduce((prev, name) => {
          return prev.replaceAll("[" + name + "]", variables[name] ?? "");
        }, expr)
      : expr;
  }
  const filter = config.filterType ?? SimpleFilter;
  const val = row[config.columnKey] ?? "";
  return filter(
    expandFilter(val),
    config.operator,
    expandFilter(config.operand)
  );
};

export const filterRowUsingConfig = (
  config: RowFilter,
  variables?: { [key: string]: string }
): ((row: Row) => boolean) => {
  return (row) =>
    config.columnFilters.reduce(
      (prev, filter) => prev && filterColumnUsingConfig(row, filter, variables),
      true
    );
};
